import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { HelmetProvider } from 'react-helmet-async';
import { useAuthStore, useNotificationStore } from './stores';
import { MainLayout, AuthLayout } from './components/layout';
import { FullPageLoader } from './components/ui';
import { useSocket } from './hooks';

import Landing from './pages/Landing';
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ForgotPassword from './pages/auth/ForgotPassword';
import VerifyEmail from './pages/auth/VerifyEmail';
import ResetPassword from './pages/auth/ResetPassword';
import ResendVerification from './pages/auth/ResendVerification';
import Dashboard from './pages/dashboard/Dashboard';
import ClientList from './pages/clients/ClientList';
import ClientForm from './pages/clients/ClientForm';
import ClientDetail from './pages/clients/ClientDetail';
import ProjectList from './pages/projects/ProjectList';
import ProjectForm from './pages/projects/ProjectForm';
import ProjectDetail from './pages/projects/ProjectDetail';
import TaskList from './pages/tasks/TaskList';
import TaskForm from './pages/tasks/TaskForm';
import TaskDetail from './pages/tasks/TaskDetail';
import InvoiceList from './pages/invoices/InvoiceList';
import InvoiceForm from './pages/invoices/InvoiceForm';
import InvoiceDetail from './pages/invoices/InvoiceDetail';
import Calendar from './pages/calendar/Calendar';
import Reports from './pages/reports/Reports';
import Chat from './pages/chat/Chat';
import Settings from './pages/settings/Settings';
import Notifications from './pages/notifications/Notifications';

function PrivateRoute({ children }) {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return <FullPageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function PublicRoute({ children }) {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return <FullPageLoader />;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function RoleProtectedRoute({ children, allowedRoles = [] }) {
  const { user, isLoading } = useAuthStore();

  if (isLoading) {
    return <FullPageLoader />;
  }

  // If no allowedRoles specified, allow all authenticated users
  if (allowedRoles.length === 0) {
    return children;
  }

  // If user role is not in allowed roles, redirect to dashboard
  if (user && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function AppRoutes() {
  useSocket();

  return (
    <Routes>
      {/* Landing Page - Accessible to everyone */}
      <Route path="/" element={<Landing />} />

      {/* Public Routes */}
      <Route element={<PublicRoute><AuthLayout /></PublicRoute>}>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/resend-verification" element={<ResendVerification />} />
      </Route>

      {/* Private Routes */}
      <Route element={<PrivateRoute><MainLayout /></PrivateRoute>}>
        <Route path="/dashboard" element={<Dashboard />} />

        {/* Client routes - Only accessible to non-client users */}
        <Route path="/clients" element={<RoleProtectedRoute allowedRoles={['admin', 'manager', 'user']}><ClientList /></RoleProtectedRoute>} />
        <Route path="/clients/new" element={<RoleProtectedRoute allowedRoles={['admin', 'manager', 'user']}><ClientForm /></RoleProtectedRoute>} />
        <Route path="/clients/:id" element={<RoleProtectedRoute allowedRoles={['admin', 'manager', 'user']}><ClientDetail /></RoleProtectedRoute>} />
        <Route path="/clients/:id/edit" element={<RoleProtectedRoute allowedRoles={['admin', 'manager', 'user']}><ClientForm /></RoleProtectedRoute>} />

        {/* Project routes - Accessible to all authenticated users */}
        <Route path="/projects" element={<ProjectList />} />
        <Route path="/projects/new" element={<RoleProtectedRoute allowedRoles={['admin', 'manager', 'user']}><ProjectForm /></RoleProtectedRoute>} />
        <Route path="/projects/:id" element={<ProjectDetail />} />
        <Route path="/projects/:id/edit" element={<RoleProtectedRoute allowedRoles={['admin', 'manager', 'user']}><ProjectForm /></RoleProtectedRoute>} />

        {/* Task routes - Only accessible to non-client users */}
        <Route path="/tasks" element={<RoleProtectedRoute allowedRoles={['admin', 'manager', 'user']}><TaskList /></RoleProtectedRoute>} />
        <Route path="/tasks/new" element={<RoleProtectedRoute allowedRoles={['admin', 'manager', 'user']}><TaskForm /></RoleProtectedRoute>} />
        <Route path="/tasks/new/:projectId" element={<RoleProtectedRoute allowedRoles={['admin', 'manager', 'user']}><TaskForm /></RoleProtectedRoute>} />
        <Route path="/tasks/:id" element={<RoleProtectedRoute allowedRoles={['admin', 'manager', 'user']}><TaskDetail /></RoleProtectedRoute>} />
        <Route path="/tasks/:id/edit" element={<RoleProtectedRoute allowedRoles={['admin', 'manager', 'user']}><TaskForm /></RoleProtectedRoute>} />

        {/* Invoice routes - Only accessible to non-client users */}
        <Route path="/invoices" element={<RoleProtectedRoute allowedRoles={['admin', 'manager', 'user']}><InvoiceList /></RoleProtectedRoute>} />
        <Route path="/invoices/new" element={<RoleProtectedRoute allowedRoles={['admin', 'manager', 'user']}><InvoiceForm /></RoleProtectedRoute>} />
        <Route path="/invoices/:id" element={<RoleProtectedRoute allowedRoles={['admin', 'manager', 'user']}><InvoiceDetail /></RoleProtectedRoute>} />
        <Route path="/invoices/:id/edit" element={<RoleProtectedRoute allowedRoles={['admin', 'manager', 'user']}><InvoiceForm /></RoleProtectedRoute>} />

        {/* Calendar, Reports, Chat - Only accessible to non-client users */}
        <Route path="/calendar" element={<RoleProtectedRoute allowedRoles={['admin', 'manager', 'user']}><Calendar /></RoleProtectedRoute>} />
        <Route path="/reports" element={<RoleProtectedRoute allowedRoles={['admin', 'manager', 'user']}><Reports /></RoleProtectedRoute>} />
        <Route path="/chat" element={<RoleProtectedRoute allowedRoles={['admin', 'manager', 'user']}><Chat /></RoleProtectedRoute>} />
        <Route path="/chat/:projectId" element={<RoleProtectedRoute allowedRoles={['admin', 'manager', 'user']}><Chat /></RoleProtectedRoute>} />

        {/* Settings and Notifications - Accessible to all authenticated users */}
        <Route path="/settings" element={<Settings />} />
        <Route path="/settings/profile" element={<Settings />} />
        <Route path="/notifications" element={<Notifications />} />
      </Route>

      {/* Redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  const { initialize } = useAuthStore();
  const { fetchUnreadCount } = useNotificationStore();
  const { isAuthenticated } = useAuthStore();

  useEffect(() => {
    initialize();
  }, [initialize]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchUnreadCount();
      const interval = setInterval(fetchUnreadCount, 60000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, fetchUnreadCount]);

  return (
    <HelmetProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#fff',
              color: '#374151',
              padding: '16px',
              borderRadius: '8px',
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
            },
            success: {
              iconTheme: {
                primary: '#10B981',
                secondary: '#fff'
              }
            },
            error: {
              iconTheme: {
                primary: '#EF4444',
                secondary: '#fff'
              }
            }
          }}
        />
      </BrowserRouter>
    </HelmetProvider>
  );
}
